import moment from 'moment';
import { handleSingleDate2RangeRangeTimeInFilterExtend } from '@intelligence/utils/dateUtils';
import {
  getTopSources,
  sectionalRankMetricSorter,
} from '@/pages/intelligence/_config/storeRank/utils';
import {
  // 时间筛选器
  RANGE_TIME as RANGE_TIME_SELECT_FILTER,
  // 单地区筛选器
  SINGLE_AREA_SELECT as SINGLE_AREA_SELECT_FILTER,
  // 多地区筛选器
  MULTIPLE_AREA_SELECT as MULTIPLE_AREA_SELECT_FILTER,
  // 组合国家筛选器
  COMPOSED_COUNTRY_SELECT as COMPOSED_COUNTRY_SELECT_FILTER,
} from '@intelligence/components/Filter/FilterBase/_config';
import {
  CONSOLE_RANK_METRIC_MONTHLY_SORT_KEYS,
  CONSOLE_PLATFORM_PLAYSTATION,
  CONSOLE_PLATFORM_NINTENDO,
  CONSOLE_TOP_BEST_SELLING,
  CONSOLE_TOP_FREE_TO_PLAY,
  CONSOLE_SOURCES_PLAYSTATION_MONTHLY_RANK,
} from '@/pages/intelligence/_config/storeRank/config';

import type { IDateTypely, IRegion, IRegionValue } from '@/pages/intelligence/types/common';
import type {
  IConsoleStoreRankPlatform,
  IConsoleStoreRankTop,
  IStoreRankDetailsRankResData,
  IStoreRankDetailsResData,
} from '@/pages/intelligence/types/storeRank';
import type { IStoreRankDetailsFilter } from '../types';

export const getCountriesValues = (regionsOptions: any[]): string[] => {
  if (!Array.isArray(regionsOptions)) return [];
  const values: Set<string> = new Set();
  for (const regionOption of regionsOptions) {
    const regionCountries = regionOption?.country_set;
    if (!Array.isArray(regionCountries)) continue;
    for (const regionCountry of regionCountries) {
      values.add(regionCountry?.country_code);
    }
  }
  return Array.from(values);
};

const rankMetricMonthlySorter = (
  a: IStoreRankDetailsRankResData,
  b: IStoreRankDetailsRankResData,
): number => {
  const aIndex = CONSOLE_RANK_METRIC_MONTHLY_SORT_KEYS.findIndex((item) => a?.name?.endsWith(item));
  const bIndex = CONSOLE_RANK_METRIC_MONTHLY_SORT_KEYS.findIndex((item) => b?.name?.endsWith(item));
  if (aIndex === -1) return 1;
  if (bIndex === -1) return 0;
  return aIndex - bIndex;
};

/**
 * 判断是否为月粒度榜单模式，月粒度榜单需要同时满足下面两个条件，月粒度榜单请求数据的接口跟日粒度榜单不一样，返回数据格式也不同，但是前端交互没什么区别
 * 1、必须是PlayStation平台
 * 2、PS5 Most Downloaded、PS4 Most Downloaded、VR Games三个榜单、或者月粒度下的Free To Play榜单（Free To Play榜单 同时支持日粒度、月粒度）
 * @param option 判断的根据信息
 * @param option.platform 平台
 * @param option.top 榜单类型
 * @param option.granularity 时间粒度
 * @return 当前模式是否为月粒度榜单模式
 */
export const getIsMonthlyRank = (option: {
  platform: string;
  top: string;
  granularity: IDateTypely;
}): boolean => {
  const { platform, top, granularity } = option ?? {};
  const flag1 = platform === CONSOLE_PLATFORM_PLAYSTATION;
  const flag2 = CONSOLE_SOURCES_PLAYSTATION_MONTHLY_RANK.includes(top);
  const flag3 = granularity === 'monthly' && top === CONSOLE_TOP_FREE_TO_PLAY;
  return flag1 && (flag2 || flag3);
};

/**
 * 判断是否为分段排名rank（即1-3、4-10、10-50、50-200 这种模式）
 * @param option 判断的根据信息
 * @param option.platform 平台
 * @param option.top 榜单类型
 * @param option.regions 国家
 * @param option.granularity 时间粒度
 * @return 是否为分段排名rank
 */
export const getIsSectionalRank = (option: {
  isMonthlyRank: boolean;
  markets: IRegionValue;
}): boolean => {
  const { isMonthlyRank, markets } = option ?? {};
  return !isMonthlyRank && markets?.length > 1;
};

const getMonthlyRankTopSources = (top: string): string[] => {
  let topTmp = top;
  // Free To Play需要特殊处理，这个榜单同时有日粒度、月粒度，根据勾选的时间粒度不同，要掉不同的接口 传不同的code
  if (topTmp === CONSOLE_TOP_FREE_TO_PLAY) {
    topTmp = `ps ${topTmp}`;
  }
  return topTmp ? [topTmp] : [];
};

const sortResData = (
  data: IStoreRankDetailsResData[],
  sorter: (a: IStoreRankDetailsRankResData, b: IStoreRankDetailsRankResData) => number,
): IStoreRankDetailsResData[] => {
  if (!Array.isArray(data)) return [];
  return data.map((item) => {
    const metrics = item?.metric?.sort(sorter) ?? [];
    return {
      ...item,
      metric: metrics,
    };
  });
};

export const handleResData = (
  data: IStoreRankDetailsResData[],
  cfg: {
    isMonthlyRank: boolean;
    isSectionalRank: boolean;
  },
): IStoreRankDetailsResData[] => {
  if (cfg?.isMonthlyRank) return sortResData(data, rankMetricMonthlySorter);
  if (cfg?.isSectionalRank) return sortResData(data, sectionalRankMetricSorter);
  return data ?? [];
};

const getValidGranularity = (
  platform: IConsoleStoreRankPlatform,
  top: IConsoleStoreRankTop,
  granularity: IDateTypely,
): IDateTypely => {
  if (platform !== CONSOLE_PLATFORM_PLAYSTATION) return 'daily';
  if (!CONSOLE_SOURCES_PLAYSTATION_MONTHLY_RANK.includes(top) && top !== CONSOLE_TOP_FREE_TO_PLAY)
    return 'daily';
  return granularity;
};

const getValidDate = (granularity: IDateTypely, date: string): string => {
  if (!date) return '';
  const m = moment(date);
  if (!m.isValid()) return '';
  if (granularity === 'monthly') {
    m.startOf('month');
  }
  return m.format('YYYY-MM-DD');
};

export const getInitialFilter = (
  isOperate: boolean,
  filterExtend: any,
  defaultFilter: any,
): any => {
  if (!isOperate) return defaultFilter || {};
  const { platform, top } = defaultFilter || {};
  const filterExtendTmp = handleSingleDate2RangeRangeTimeInFilterExtend(filterExtend);
  const rangeTimeFilterExtend = filterExtendTmp?.[RANGE_TIME_SELECT_FILTER];
  const singleAreaFilterExtend = filterExtendTmp?.[SINGLE_AREA_SELECT_FILTER];
  const multipleAreaFilterExtend = filterExtendTmp?.[MULTIPLE_AREA_SELECT_FILTER];
  const composedCountryFilterExtend = filterExtendTmp?.[COMPOSED_COUNTRY_SELECT_FILTER];

  const granularity = rangeTimeFilterExtend?.granularity || 'daily';
  const validGranularity = getValidGranularity(platform, top, granularity);
  const validStartDate = getValidDate(validGranularity, rangeTimeFilterExtend?.start_date);
  const validEndDate = getValidDate(validGranularity, rangeTimeFilterExtend?.end_date);
  const areasFilterExtend = singleAreaFilterExtend?.value
    ? [singleAreaFilterExtend?.value]
    : multipleAreaFilterExtend?.value;
  // console.log(filterExtendTmp)
  return {
    ...defaultFilter,
    granularity: validGranularity || defaultFilter?.granularity,
    start_date: validStartDate || defaultFilter?.start_date,
    end_date: validEndDate || defaultFilter?.end_date,
    regions: areasFilterExtend || defaultFilter?.regions,
    markets: composedCountryFilterExtend?.value?.countries || defaultFilter?.markets,
  };
};

export const getParams = (
  filter: IStoreRankDetailsFilter,
  filterPayload?: IStoreRankDetailsFilter,
) => {
  const granularity = filterPayload?.granularity ?? filter?.granularity ?? 'daily';
  const startDate = filterPayload?.start_date ?? filter?.start_date ?? '';
  const endDate = filterPayload?.end_date ?? filter?.end_date ?? '';
  const platform = filterPayload?.platform ?? filter?.platform ?? CONSOLE_PLATFORM_PLAYSTATION;
  const top = filterPayload?.top ?? filter?.top ?? CONSOLE_TOP_BEST_SELLING;
  const regions = filterPayload?.regions ?? filter?.regions ?? [];
  const markets = filterPayload?.markets ?? filter?.markets ?? [];
  const isMonthlyRank = getIsMonthlyRank({
    platform,
    top,
    granularity,
  });
  const sources = isMonthlyRank ? getMonthlyRankTopSources(top) : getTopSources(platform, top);

  return {
    granularity,
    startDate,
    endDate,
    sources,
    regions,
    markets, // platform === CONSOLE_PLATFORM_NINTENDO ? [] : markets,
  };
};
